'use client';

import { Container } from '@/components/primitives/Container/Container';
import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { IEnhancedCta, IStandardContentPageComponents } from '@/.generated';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { ButtonWrapper } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { defaultVariants } from './Notifications.styles';

type NotificationsProps = WithMetadata<IBaseComponentProps> &
  IStandardContentPageComponents['notifications'];

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

// Formats an ISO date string ("YYYY-MM-DD") into weekday, month day, year (e.g. "Mon, Oct 5, 2026") without timezone drift
const formatLastPublished = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
};

const Default = (props: NotificationsProps) => {
  const {
    componentName,
    _metadata,
    inline_navigation_id,
    publish_date,
    topic_code,
    hide_component,
  } = props;
  const { siteSettings } = useSiteSettings();
  const { globalLabels } = useGlobalLabels();
  const formattedDate = formatLastPublished(publish_date);
  const zetaBaseUrl = siteSettings.base_configurations?.zeta_base_url;

  if (hide_component) return null;

  // Only build the subscribe URL when both pieces are present — otherwise we'd
  // produce a broken href like "undefinedfoo" and quietly ship a dead button.
  const subscribeUrl =
    zetaBaseUrl && topic_code ? `${zetaBaseUrl}${encodeURIComponent(topic_code)}` : undefined;

  // Center the single visible slot when only one of date/button is shown
  const alignment = formattedDate && subscribeUrl ? 'paired' : 'centered';
  const { wrapper, lastUpdated, actionWrapper, subscribeButton } = defaultVariants({ alignment });

  return (
    <Container componentName={componentName} metaId={_metadata?.uid} id={inline_navigation_id}>
      <div className={wrapper()}>
        {formattedDate && (
          <PlainTextWrapper
            content={`${globalLabels.page_last_updated_on_label ?? ''} ${formattedDate}`.trim()}
            cslpAttribute={globalLabels.$?.page_last_updated_on_label}
            className={lastUpdated()}
          />
        )}
        {subscribeUrl && (
          <div className={actionWrapper()}>
            <ButtonWrapper
              cta={
                {
                  link: {
                    title: globalLabels.subscribe_to_updates_label ?? '',
                    href: subscribeUrl,
                  },
                  opens_in_new_tab: true,
                  cta_variant: 'primary-outline',
                  cta_size: 'variable',
                  has_font_awesome_icons: true,
                  left_font_awesome_icon_class: 'fa-solid fa-bell',
                  $: {
                    link: globalLabels.$?.subscribe_to_updates_label,
                  },
                } as IEnhancedCta
              }
              showNewTabIcon={false}
              className={subscribeButton()}
            />
          </div>
        )}
      </div>
    </Container>
  );
};

const variants = {
  Default,
};

export const Notifications = (props: NotificationsProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
